// src/admin/pages/labels/LabelPrinting.tsx
//
// Prints barcode labels for products, downloadable as a PDF. Handles the two
// stock-tracking modes distinctly (see enums/product.ts requiresSerialTracking):
//   - Serial-tracked lines: one label per unique unit, barcode = that unit's
//     serial number, pulled from the chosen warehouse's WarehouseInventory.
//   - Ordinary lines: one label repeated N times, barcode = the SKU.

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Autocomplete,
  TextField,
  Button,
  Stack,
  Grid,
  Divider,
  Chip,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Label as LabelIcon,
} from '@mui/icons-material';
import { ProductService } from '../product/productService';
import { WarehouseInventoryService } from '../product/WarehouseInventoryService';
import { WarehouseService } from '../warehouse/warehouseService';
import { Product, ProductVariant, Warehouse, calculateDiscountedPrice } from '../../../enums/product';
import { useCompany } from '../../../contexts/CompanyContext';
import { getCurrencySymbol } from '../../../util/productUtils';
import { formatCurrency } from '../../../util/Formatcurrency';
import { LabelPDFGenerator, LabelQueueItem } from './labelPDFGenerator';

interface QueueGroup {
  id: string;
  productName: string;
  variantText?: string;
  isSerial: boolean;
  price?: number;
  codes: string[];
}

interface WarehouseSerials {
  warehouseId: string;
  warehouseName: string;
  serials: string[];
}

const variantLabel = (v: ProductVariant): string => v.variantSelections.map((s) => s.variantValue).join(' - ');

const LabelPrinting: React.FC = () => {
  const { companyProfile } = useCompany();
  const currency = companyProfile?.currency || 'LKR';
  const currencySymbol = getCurrencySymbol(currency);

  const [products, setProducts] = useState<Product[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [error, setError] = useState('');

  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [selectedVariant, setSelectedVariant] = useState<ProductVariant | null>(null);

  const [quantity, setQuantity] = useState('1');

  const [loadingSerials, setLoadingSerials] = useState(false);
  const [warehouseOptions, setWarehouseOptions] = useState<WarehouseSerials[]>([]);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState('');
  const [selectedSerials, setSelectedSerials] = useState<Set<string>>(new Set());

  const [queueGroups, setQueueGroups] = useState<QueueGroup[]>([]);

  const [labelWidthMm, setLabelWidthMm] = useState('45');
  const [labelHeightMm, setLabelHeightMm] = useState('25');
  const [columns, setColumns] = useState('4');
  const [includePrice, setIncludePrice] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [productList, warehouseList] = await Promise.all([
          ProductService.getProductsForDropdown(),
          WarehouseService.getActiveWarehouses(),
        ]);
        setProducts(productList);
        setWarehouses(warehouseList);
      } catch (err) {
        console.error('Failed to load products/warehouses', err);
        setError('Failed to load products or warehouses. Please refresh the page.');
      } finally {
        setLoadingProducts(false);
      }
    })();
  }, []);

  const hasVariants = !!selectedProduct?.hasVariants && (selectedProduct?.variants?.length ?? 0) > 0;
  const isSerialTracked = selectedVariant
    ? !!selectedVariant.requiresSerialTracking
    : !!selectedProduct?.requiresSerialTracking;

  const lineReady = !!selectedProduct && (!hasVariants || !!selectedVariant);

  const currentSku = selectedVariant?.sku || selectedProduct?.sku || '';
  const currentPrice = useMemo(() => {
    if (selectedVariant) return calculateDiscountedPrice(selectedVariant.price, selectedVariant.discount);
    if (selectedProduct) return calculateDiscountedPrice(selectedProduct.price, selectedProduct.discount);
    return undefined;
  }, [selectedProduct, selectedVariant]);
  const currentStock = selectedVariant ? selectedVariant.stockQuantity : selectedProduct?.stockQuantity ?? 0;

  // Reset the per-line configuration whenever the picked product/variant changes.
  useEffect(() => {
    setQuantity(String(currentStock > 0 ? currentStock : 1));
    setWarehouseOptions([]);
    setSelectedWarehouseId('');
    setSelectedSerials(new Set());

    if (!lineReady || !isSerialTracked || !selectedProduct) return;

    (async () => {
      setLoadingSerials(true);
      try {
        const results = await Promise.all(
          warehouses.map(async (w) => {
            const inv = await WarehouseInventoryService.getProductInventory(
              w.id!,
              selectedProduct.id!,
              selectedVariant?.id
            );
            return { warehouse: w, inv };
          })
        );
        const withStock: WarehouseSerials[] = results
          .filter((r) => (r.inv?.availableSerials?.length ?? 0) > 0)
          .map((r) => ({
            warehouseId: r.warehouse.id!,
            warehouseName: r.warehouse.name,
            serials: r.inv!.availableSerials!,
          }));
        setWarehouseOptions(withStock);
        if (withStock.length > 0) {
          setSelectedWarehouseId(withStock[0].warehouseId);
          setSelectedSerials(new Set(withStock[0].serials));
        }
      } catch (err) {
        console.error('Failed to load warehouse serials', err);
        setError('Failed to load serial numbers for this product.');
      } finally {
        setLoadingSerials(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProduct?.id, selectedVariant?.id, isSerialTracked]);

  const activeWarehouseSerials = warehouseOptions.find((w) => w.warehouseId === selectedWarehouseId)?.serials ?? [];

  const toggleSerial = (serial: string) => {
    setSelectedSerials((prev) => {
      const next = new Set(prev);
      if (next.has(serial)) next.delete(serial);
      else next.add(serial);
      return next;
    });
  };

  const handleAddToQueue = () => {
    if (!selectedProduct) return;
    const variantText = selectedVariant ? variantLabel(selectedVariant) : undefined;

    if (isSerialTracked) {
      const codes = Array.from(selectedSerials);
      if (codes.length === 0) return;
      setQueueGroups((prev) => [
        ...prev,
        {
          id: `${selectedProduct.id}-${selectedVariant?.id || ''}-${Date.now()}`,
          productName: selectedProduct.name,
          variantText,
          isSerial: true,
          price: includePrice ? currentPrice : undefined,
          codes,
        },
      ]);
    } else {
      const qty = Math.max(1, parseInt(quantity, 10) || 0);
      setQueueGroups((prev) => [
        ...prev,
        {
          id: `${selectedProduct.id}-${selectedVariant?.id || ''}-${Date.now()}`,
          productName: selectedProduct.name,
          variantText,
          isSerial: false,
          price: includePrice ? currentPrice : undefined,
          codes: Array(qty).fill(currentSku),
        },
      ]);
    }

    setSelectedProduct(null);
    setSelectedVariant(null);
  };

  const handleRemoveGroup = (id: string) => {
    setQueueGroups((prev) => prev.filter((g) => g.id !== id));
  };

  const flatQueue: LabelQueueItem[] = useMemo(
    () =>
      queueGroups.flatMap((g) =>
        g.codes.map((code) => ({
          productName: g.productName,
          variantText: g.variantText,
          code,
          isSerial: g.isSerial,
          price: g.price,
        }))
      ),
    [queueGroups]
  );

  const layoutOptions = {
    labelWidthMm: parseFloat(labelWidthMm) || 45,
    labelHeightMm: parseFloat(labelHeightMm) || 25,
    columns: Math.max(1, parseInt(columns, 10) || 1),
    includePrice,
    currency,
  };

  const pageCount = LabelPDFGenerator.countPages(flatQueue.length, layoutOptions);

  const handleGeneratePDF = () => {
    LabelPDFGenerator.generatePDF(flatQueue, layoutOptions);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <LabelIcon color="primary" />
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Product Labels</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Generate printable barcode labels for products. Serial-tracked products get one label per unit
        (using that unit's serial number); ordinary products get as many identical labels as you need.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Add product to print queue</Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={products}
              getOptionLabel={(o) => o.name}
              value={selectedProduct}
              loading={loadingProducts}
              onChange={(_, value) => {
                setSelectedProduct(value);
                setSelectedVariant(null);
              }}
              renderInput={(params) => <TextField {...params} label="Product" placeholder="Search product…" />}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box>
                    <Typography variant="body2">{option.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.hasVariants ? 'Has Variants' : `SKU: ${option.sku}`}
                      {option.requiresSerialTracking ? ' · Serial-tracked' : ''}
                    </Typography>
                  </Box>
                </li>
              )}
            />
          </Grid>

          {hasVariants && (
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={selectedProduct?.variants || []}
                getOptionLabel={(o) => variantLabel(o)}
                value={selectedVariant}
                onChange={(_, value) => setSelectedVariant(value)}
                renderInput={(params) => <TextField {...params} label="Variant" placeholder="Select variant…" />}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Box>
                      <Typography variant="body2">{variantLabel(option)}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        SKU: {option.sku} · {formatCurrency(option.price, currencySymbol)}
                        {option.requiresSerialTracking ? ' · Serial-tracked' : ''}
                      </Typography>
                    </Box>
                  </li>
                )}
              />
            </Grid>
          )}

          {lineReady && (
            <Grid item xs={12}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
                <Chip size="small" label={`SKU: ${currentSku}`} />
                {currentPrice !== undefined && <Chip size="small" label={formatCurrency(currentPrice, currencySymbol)} />}
                <Chip size="small" label={`Stock: ${currentStock}`} />
                <Chip
                  size="small"
                  color={isSerialTracked ? 'secondary' : 'default'}
                  label={isSerialTracked ? 'Serial-tracked' : 'Standard'}
                />
              </Stack>

              {isSerialTracked ? (
                loadingSerials ? (
                  <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={16} /><Typography variant="body2">Loading serial numbers…</Typography></Stack>
                ) : warehouseOptions.length === 0 ? (
                  <Alert severity="warning">No warehouse currently holds serial-tracked stock for this item.</Alert>
                ) : (
                  <Box>
                    <TextField
                      select
                      size="small"
                      label="Warehouse"
                      value={selectedWarehouseId}
                      onChange={(e) => {
                        const wid = e.target.value;
                        setSelectedWarehouseId(wid);
                        const opt = warehouseOptions.find((w) => w.warehouseId === wid);
                        setSelectedSerials(new Set(opt?.serials || []));
                      }}
                      sx={{ minWidth: 220, mb: 1.5 }}
                    >
                      {warehouseOptions.map((w) => (
                        <MenuItem key={w.warehouseId} value={w.warehouseId}>
                          {w.warehouseName} ({w.serials.length} available)
                        </MenuItem>
                      ))}
                    </TextField>

                    <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                      <Button size="small" onClick={() => setSelectedSerials(new Set(activeWarehouseSerials))}>Select all</Button>
                      <Button size="small" onClick={() => setSelectedSerials(new Set())}>Select none</Button>
                    </Stack>

                    <Box sx={{ maxHeight: 220, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
                      <Grid container spacing={0.5}>
                        {activeWarehouseSerials.map((serial) => (
                          <Grid item xs={6} sm={4} md={3} key={serial}>
                            <FormControlLabel
                              control={<Checkbox size="small" checked={selectedSerials.has(serial)} onChange={() => toggleSerial(serial)} />}
                              label={<Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{serial}</Typography>}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </Box>

                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      sx={{ mt: 2 }}
                      disabled={selectedSerials.size === 0}
                      onClick={handleAddToQueue}
                    >
                      Add {selectedSerials.size} label{selectedSerials.size === 1 ? '' : 's'} to queue
                    </Button>
                  </Box>
                )
              ) : (
                <Stack direction="row" spacing={2} alignItems="center">
                  <TextField
                    label="Quantity"
                    type="number"
                    size="small"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    inputProps={{ min: 1 }}
                    sx={{ width: 140 }}
                  />
                  <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddToQueue}>
                    Add to queue
                  </Button>
                </Stack>
              )}
            </Grid>
          )}
        </Grid>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Print queue ({flatQueue.length} label{flatQueue.length === 1 ? '' : 's'})
          </Typography>
          {queueGroups.length > 0 && (
            <Button size="small" color="error" onClick={() => setQueueGroups([])}>Clear all</Button>
          )}
        </Stack>

        {queueGroups.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No labels queued yet.</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Product</TableCell>
                <TableCell>Variant</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Count</TableCell>
                <TableCell>Codes</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {queueGroups.map((g) => (
                <TableRow key={g.id}>
                  <TableCell>{g.productName}</TableCell>
                  <TableCell>{g.variantText || '—'}</TableCell>
                  <TableCell>{g.isSerial ? 'Serial' : 'Standard'}</TableCell>
                  <TableCell>{g.codes.length}</TableCell>
                  <TableCell sx={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {g.isSerial ? g.codes.join(', ') : g.codes[0]}
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" color="error" onClick={() => handleRemoveGroup(g.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Label layout</Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={6} sm={3}>
            <TextField
              label="Label width (mm)"
              type="number"
              size="small"
              fullWidth
              value={labelWidthMm}
              onChange={(e) => setLabelWidthMm(e.target.value)}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              label="Label height (mm)"
              type="number"
              size="small"
              fullWidth
              value={labelHeightMm}
              onChange={(e) => setLabelHeightMm(e.target.value)}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              label="Columns per row"
              type="number"
              size="small"
              fullWidth
              value={columns}
              onChange={(e) => setColumns(e.target.value)}
              inputProps={{ min: 1 }}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <FormControlLabel
              control={<Checkbox checked={includePrice} onChange={(e) => setIncludePrice(e.target.checked)} />}
              label="Include price"
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {flatQueue.length} label{flatQueue.length === 1 ? '' : 's'} → {pageCount} page{pageCount === 1 ? '' : 's'} (A4)
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<PictureAsPdfIcon />}
            disabled={flatQueue.length === 0}
            onClick={handleGeneratePDF}
          >
            Download PDF
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
};

export default LabelPrinting;
